import { DEFAULT_LOOK, type TankBody, type TankDecal, type TankLook } from '@tanky/shared';

export interface TankDrawOpts {
  alive: boolean;
  /** Světla bliknou (emote). */
  blink?: boolean;
  /** Posun nahoru v px (emote poskočení). */
  hop?: number;
  /** Zpětný ráz po výstřelu 0–1 (hlaveň zajede, tank cukne a zhoupne se). */
  recoil?: number;
  /** Detailní kresba (velké přiblížení, editor). */
  detail?: boolean;
  /** Nahraný obrázek hráče – kreslí se jen v detailu. */
  emblem?: HTMLImageElement | null;
  /** Vítr a čas – pro vlaječku na anténě v detailu. */
  wind?: number;
  time?: number;
}

const TRACK = '#23252c';
const TRACK_HI = '#50535e';
const BARREL_DARK = '#1d1f26';

// ---------- Barvy ----------

function parseHex(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Zesvětlí (k > 0) nebo ztmaví (k < 0) barvu. */
export function shade(hex: string, k: number): string {
  const [r, g, b] = parseHex(hex);
  const f = (c: number) => Math.round(k >= 0 ? c + (255 - c) * k : c * (1 + k));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

// ---------- Obrázky na tanku ----------

const emblemCache = new Map<string, HTMLImageElement | null>();

/** Obrázek hráče (načte se líně; dokud se nenačte, vrací null). */
export function emblemImage(userId: string, look: TankLook | undefined | null): HTMLImageElement | null {
  if (!look?.emblem) return null;
  const key = `${userId}:${look.emblem}`;
  if (!emblemCache.has(key)) {
    const img = new Image();
    img.decoding = 'async';
    emblemCache.set(key, null);
    img.onload = () => emblemCache.set(key, img);
    img.src = `/api/emblems/${encodeURIComponent(userId)}?v=${look.emblem}`;
  }
  return emblemCache.get(key) ?? null;
}

/** Po nahrání nového obrázku vlastní cache zahodit. */
export function putEmblem(userId: string, version: number, img: HTMLImageElement) {
  emblemCache.set(`${userId}:${version}`, img);
}

// ---------- Geometrie typů tanků ----------

interface BodyDef {
  pivot: [number, number];
  barrelLen: number;
  barrelW: number;
  turret: (ctx: CanvasRenderingContext2D, x: number, y: number) => void;
  hull: (ctx: CanvasRenderingContext2D, x: number, y: number) => void;
  /** Horní hrana korby (pro nýty/lesk) [x0, x1, y]. */
  hullTop: [number, number, number];
  running: { kind: 'tracks'; half: number; h: number } | { kind: 'wheels'; xs: number[]; r: number };
  decal: [number, number, number];
  light: [number, number];
  antenna: [number, number];
  hatch: [number, number];
}

const BODIES: Record<TankBody, BodyDef> = {
  classic: {
    pivot: [0, -8],
    barrelLen: 14,
    barrelW: 2,
    turret: (c, x, y) => c.arc(x, y - 7, 5.5, Math.PI, 0),
    hull: (c, x, y) => {
      c.moveTo(x - 10, y - 6);
      c.lineTo(x + 10, y - 6);
      c.lineTo(x + 8, y - 3);
      c.lineTo(x - 8, y - 3);
      c.closePath();
    },
    hullTop: [-9, 9, -6],
    running: { kind: 'tracks', half: 10, h: 5 },
    decal: [-1, -8.5, 1.8],
    light: [9, -5],
    antenna: [-4, -11],
    hatch: [1, -11.8],
  },
  heavy: {
    pivot: [1, -10],
    barrelLen: 16,
    barrelW: 4,
    turret: (c, x, y) => c.roundRect(x - 7, y - 15, 13, 7, 3),
    hull: (c, x, y) => {
      c.moveTo(x - 13, y - 9);
      c.lineTo(x + 13, y - 9);
      c.lineTo(x + 11, y - 4);
      c.lineTo(x - 11, y - 4);
      c.closePath();
    },
    hullTop: [-12, 12, -9],
    running: { kind: 'tracks', half: 14, h: 6 },
    decal: [-5, -6.5, 2.2],
    light: [12, -7],
    antenna: [-5, -15],
    hatch: [-2, -15],
  },
  light: {
    pivot: [0, -8],
    barrelLen: 12,
    barrelW: 2,
    turret: (c, x, y) => c.arc(x, y - 7.5, 4, Math.PI, 0),
    hull: (c, x, y) => {
      c.moveTo(x - 10, y - 7);
      c.quadraticCurveTo(x, y - 9, x + 11, y - 6);
      c.lineTo(x + 8, y - 3);
      c.lineTo(x - 9, y - 3);
      c.closePath();
    },
    hullTop: [-8, 8, -7.4],
    running: { kind: 'wheels', xs: [-6, 0, 6], r: 2.6 },
    decal: [-5, -5, 1.6],
    light: [10, -5.5],
    antenna: [-3, -11],
    hatch: [0.5, -11.3],
  },
  howitzer: {
    pivot: [-2, -10],
    barrelLen: 19,
    barrelW: 2.6,
    turret: (c, x, y) => {
      c.moveTo(x - 11, y - 13);
      c.lineTo(x + 5, y - 13);
      c.lineTo(x + 11, y - 7);
      c.lineTo(x + 11, y - 3);
      c.lineTo(x - 11, y - 3);
      c.closePath();
    },
    hull: () => {},
    hullTop: [-10, 4, -13],
    running: { kind: 'tracks', half: 11, h: 5 },
    decal: [-5, -10, 1.8],
    light: [10, -6],
    antenna: [-8, -13],
    hatch: [-3, -13],
  },
};

// ---------- Kreslení ----------

function drawBarrel(ctx: CanvasRenderingContext2D, px: number, py: number, angle: number, len: number, width: number, color: string, detail: boolean) {
  const a = (angle * Math.PI) / 180;
  const dx = Math.cos(a);
  const dy = -Math.sin(a);
  const ex = px + dx * len;
  const ey = py + dy * len;
  ctx.lineCap = 'round';
  ctx.strokeStyle = BARREL_DARK;
  ctx.lineWidth = width + 2;
  ctx.beginPath();
  ctx.moveTo(px, py);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
  if (!detail) return;
  // Lesk po délce hlavně
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = Math.max(0.5, width * 0.3);
  ctx.beginPath();
  ctx.moveTo(px - dy * width * 0.2, py + dx * width * 0.2 - width * 0.15);
  ctx.lineTo(ex - dx * 2 - dy * width * 0.2, ey - dy * 2 + dx * width * 0.2 - width * 0.15);
  ctx.stroke();
  // Úsťová brzda
  ctx.strokeStyle = BARREL_DARK;
  ctx.lineWidth = width + 2.2;
  ctx.lineCap = 'butt';
  ctx.beginPath();
  ctx.moveTo(ex - dx * 2.2, ey - dy * 2.2);
  ctx.lineTo(ex + dx * 0.6, ey + dy * 0.6);
  ctx.stroke();
  // Objímka u věže
  ctx.lineWidth = width + 1.6;
  ctx.beginPath();
  ctx.moveTo(px + dx * 2, py + dy * 2);
  ctx.lineTo(px + dx * 3.6, py + dy * 3.6);
  ctx.stroke();
  ctx.lineCap = 'round';
}

function drawRunning(ctx: CanvasRenderingContext2D, x: number, y: number, def: BodyDef, detail: boolean) {
  const r = def.running;
  if (r.kind === 'wheels') {
    for (const wx of r.xs) {
      ctx.fillStyle = TRACK;
      ctx.beginPath();
      ctx.arc(x + wx, y - 1.5, r.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = detail ? '#8a8f9c' : TRACK_HI;
      if (detail) {
        ctx.beginPath();
        ctx.arc(x + wx, y - 1.5, r.r * 0.45, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#2d3038';
        ctx.beginPath();
        ctx.arc(x + wx, y - 1.5, r.r * 0.15, 0, Math.PI * 2);
        ctx.fill();
      } else ctx.fillRect(x + wx - 0.7, y - 2.2, 1.4, 1.4);
    }
    return;
  }
  const top = y + 1 - r.h;
  ctx.fillStyle = TRACK;
  ctx.beginPath();
  ctx.roundRect(x - r.half, top, r.half * 2, r.h, r.h / 2);
  ctx.fill();
  if (!detail) {
    ctx.fillStyle = TRACK_HI;
    for (let i = -r.half + 3; i <= r.half - 3; i += 3.5) ctx.fillRect(x + i - 0.8, top + 1.5, 1.6, r.h - 3);
    return;
  }
  // Články pásu
  ctx.fillStyle = '#3a3d47';
  for (let i = -r.half + 1.2; i < r.half - 1; i += 1.6) {
    ctx.fillRect(x + i, top, 0.8, 0.8);
    ctx.fillRect(x + i, top + r.h - 0.8, 0.8, 0.8);
  }
  // Pojezdová kola
  const wr = r.h / 2 - 0.9;
  const n = Math.max(3, Math.round((r.half * 2 - r.h) / (wr * 2 + 0.6)));
  for (let k = 0; k < n; k++) {
    const wx = x - r.half + r.h / 2 + ((r.half * 2 - r.h) * k) / (n - 1);
    const wy = top + r.h / 2;
    ctx.fillStyle = '#6d7280';
    ctx.beginPath();
    ctx.arc(wx, wy, wr, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#2d3038';
    ctx.beginPath();
    ctx.arc(wx, wy, wr * 0.35, 0, Math.PI * 2);
    ctx.fill();
  }
}

function decal(ctx: CanvasRenderingContext2D, kind: TankDecal, cx: number, cy: number, s: number, color: string) {
  if (kind === 'none') return;
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = s * 0.28;
  ctx.beginPath();
  switch (kind) {
    case 'star':
      for (let i = 0; i < 10; i++) {
        const r = i % 2 ? s * 0.42 : s;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.fill();
      break;
    case 'skull':
      ctx.arc(cx, cy - s * 0.15, s * 0.75, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(cx - s * 0.4, cy + s * 0.3, s * 0.8, s * 0.5);
      ctx.fillStyle = '#111';
      ctx.beginPath();
      ctx.arc(cx - s * 0.3, cy - s * 0.2, s * 0.2, 0, Math.PI * 2);
      ctx.arc(cx + s * 0.3, cy - s * 0.2, s * 0.2, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'stripes':
      for (const d of [-s * 0.6, 0, s * 0.6]) {
        ctx.moveTo(cx + d - s * 0.35, cy + s * 0.7);
        ctx.lineTo(cx + d + s * 0.35, cy - s * 0.7);
      }
      ctx.stroke();
      break;
    case 'flame':
      ctx.moveTo(cx - s, cy + s * 0.6);
      ctx.quadraticCurveTo(cx - s * 0.6, cy - s * 0.2, cx - s * 0.1, cy - s);
      ctx.quadraticCurveTo(cx, cy - s * 0.1, cx + s * 0.4, cy - s * 0.5);
      ctx.quadraticCurveTo(cx + s * 0.4, cy, cx + s, cy - s * 0.2);
      ctx.quadraticCurveTo(cx + s * 0.9, cy + s * 0.4, cx + s * 0.6, cy + s * 0.6);
      ctx.closePath();
      ctx.fill();
      break;
    case 'target':
      ctx.lineWidth = s * 0.22;
      ctx.arc(cx, cy, s * 0.8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, s * 0.3, 0, Math.PI * 2);
      ctx.fill();
      break;
  }
  ctx.restore();
}

function drawEmblem(ctx: CanvasRenderingContext2D, img: HTMLImageElement, cx: number, cy: number, r: number, ring: string) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
  ctx.restore();
  ctx.strokeStyle = ring;
  ctx.lineWidth = Math.max(0.35, r * 0.18);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
}

/**
 * Nakreslí tank stojící v bodě (x, y). Souřadnice jsou ve světě hry
 * (tank je zhruba 20–28 px široký), náhledy si ho zvětší přes ctx.scale.
 */
export function drawTank(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  lookIn: TankLook | undefined,
  color: string,
  opts: TankDrawOpts,
) {
  const rec = opts.alive ? (opts.recoil ?? 0) : 0;
  if (rec <= 0) return drawTankBody(ctx, x, y, angle, lookIn, color, opts);
  // Zpětný ráz: tank cukne proti směru hlavně a zadek se přikrčí
  const dir = Math.cos((angle * Math.PI) / 180) >= 0 ? 1 : -1;
  ctx.save();
  ctx.translate(x - dir * rec * 2.5, y);
  ctx.rotate(-dir * rec * 0.08);
  drawTankBody(ctx, 0, 0, angle, lookIn, color, opts, rec * 3.5);
  ctx.restore();
}

function drawTankBody(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  lookIn: TankLook | undefined,
  color: string,
  opts: TankDrawOpts,
  /** O kolik px hlaveň zajela zpět. */
  slide = 0,
) {
  const look = lookIn ?? { ...DEFAULT_LOOK, color };
  const c = look.color;
  y -= opts.hop ?? 0;

  if (!opts.alive) {
    drawWreck(ctx, x, y, c);
    return;
  }

  const def = BODIES[look.body] ?? BODIES.classic;
  const detail = !!opts.detail;

  // Anténa s vlaječkou (za věží)
  if (detail) {
    const [ax, ay] = [x + def.antenna[0], y + def.antenna[1]];
    const top = ay - 9;
    ctx.strokeStyle = '#2b2e36';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(ax - 0.6, top);
    ctx.stroke();
    const wind = opts.wind ?? 0;
    const dir = wind < 0 ? -1 : 1;
    const flap = Math.sin((opts.time ?? 0) * (4 + Math.abs(wind) / 8)) * 0.8;
    const len = 3 + Math.min(1, Math.abs(wind) / 50) * 1.5;
    ctx.fillStyle = look.accent;
    ctx.beginPath();
    ctx.moveTo(ax - 0.6, top);
    ctx.lineTo(ax - 0.6 + dir * len, top + 1 + flap);
    ctx.lineTo(ax - 0.6, top + 2.4);
    ctx.closePath();
    ctx.fill();
  }

  const ar = (angle * Math.PI) / 180;
  drawBarrel(ctx, x + def.pivot[0] - Math.cos(ar) * slide, y + def.pivot[1] + Math.sin(ar) * slide, angle, def.barrelLen, def.barrelW, c, detail);

  const fillBody = (path: (ctx: CanvasRenderingContext2D, x: number, y: number) => void, y0: number, y1: number) => {
    if (detail) {
      const g = ctx.createLinearGradient(0, y + y0, 0, y + y1);
      g.addColorStop(0, shade(c, 0.35));
      g.addColorStop(0.45, c);
      g.addColorStop(1, shade(c, -0.4));
      ctx.fillStyle = g;
    } else ctx.fillStyle = c;
    ctx.beginPath();
    path(ctx, x, y);
    ctx.fill();
    if (detail) {
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.lineWidth = 0.4;
      ctx.stroke();
    }
  };
  fillBody(def.turret, -16, -3);
  fillBody(def.hull, -10, -3);

  const [tx0, tx1, ty] = def.hullTop;
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillRect(x + tx0, y + ty, tx1 - tx0, detail ? 0.7 : 1.5);

  if (detail) {
    // Nýty a spára
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    for (let i = tx0 + 1.5; i <= tx1 - 1.5; i += 3) {
      ctx.beginPath();
      ctx.arc(x + i, y + ty + 1.4, 0.35, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 0.3;
    ctx.beginPath();
    ctx.moveTo(x + tx0 + 1, y - 4.2);
    ctx.lineTo(x + tx1 - 1, y - 4.2);
    ctx.stroke();
    // Poklop
    ctx.fillStyle = shade(c, -0.3);
    ctx.beginPath();
    ctx.ellipse(x + def.hatch[0], y + def.hatch[1], 1.8, 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const [dx, dy, ds] = def.decal;
  if (detail && opts.emblem) drawEmblem(ctx, opts.emblem, x + dx, y + dy, ds * 1.25, look.accent);
  else decal(ctx, look.decal, x + dx, y + dy, ds, look.accent);

  drawRunning(ctx, x, y, def, detail);

  const light = { x: x + def.light[0], y: y + def.light[1] };
  if (detail) {
    ctx.fillStyle = '#fff6c8';
    ctx.beginPath();
    ctx.arc(light.x, light.y, 0.8, 0, Math.PI * 2);
    ctx.fill();
  }

  if (opts.blink) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const g = ctx.createRadialGradient(light.x, light.y, 0, light.x, light.y, 18);
    g.addColorStop(0, 'rgba(255,250,200,0.95)');
    g.addColorStop(1, 'rgba(255,220,120,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(light.x, light.y, 18, 0, Math.PI * 2);
    ctx.fill();
    const g2 = ctx.createRadialGradient(x, y - 8, 0, x, y - 8, 22);
    g2.addColorStop(0, 'rgba(255,255,255,0.35)');
    g2.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g2;
    ctx.beginPath();
    ctx.arc(x, y - 8, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawWreck(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
  for (const shadeColor of [color, 'rgba(15,12,10,0.6)']) {
    ctx.fillStyle = shadeColor;
    ctx.beginPath();
    ctx.moveTo(x - 10, y);
    ctx.lineTo(x - 8, y - 6);
    ctx.lineTo(x + 7, y - 7);
    ctx.lineTo(x + 10, y);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x - 1, y - 6, 4.5, Math.PI, 0);
    ctx.fill();
  }
  ctx.strokeStyle = 'rgba(20,16,14,0.9)';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(x + 2, y - 8);
  ctx.lineTo(x + 12, y - 3);
  ctx.stroke();
}

/** Malý statický náhled tanku do DOM (čekárna, žebříček). */
export function tankThumb(look: TankLook | null | undefined, fallbackColor: string, size = 44, userId?: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  c.width = size * dpr;
  c.height = size * 0.7 * dpr;
  c.style.width = `${size}px`;
  c.style.height = `${size * 0.7}px`;
  const ctx = c.getContext('2d')!;
  // 40 × 28 jednotek světa – vejde se i dlouhá hlaveň houfnice
  const k = (size / 40) * dpr;
  const draw = () => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.setTransform(k, 0, 0, k, 0, 0);
    const emblem = userId ? emblemImage(userId, look) : null;
    drawTank(ctx, 18, 26, 50, look ?? undefined, fallbackColor, { alive: true, detail: size >= 40, emblem });
  };
  draw();
  // Obrázek se může dotáhnout později – překreslit
  if (userId && look?.emblem && !emblemImage(userId, look)) setTimeout(draw, 600);
  return c;
}
